import { buildCellKey } from 'components/CellAnnotationValueViewer/types';
import type { PreFxAmountSign } from 'components/CellAnnotationValueViewer/types';
import { useCellAnnotationStore } from 'store/useCellAnnotationStore';
import { CASH_FLOW_TYPE, CashFlowColumnAlias } from './constants';
import type { CashFlowLedgerItem } from './types';

// Sign rule for the Cash Flow Ledger: amounts are stored in cash-flow sign (investments negative).
// Investment rows display the negation of the stored amount; Proceeds display as stored. Saving
// applies the same negation, so the stored sign always follows the display.

const isNumeric = (value: unknown): value is number | string =>
  (typeof value === 'number' || (typeof value === 'string' && value.trim() !== ''))
  && Number.isFinite(Number(value));

// Text amounts are negated as text so 15-decimal values keep their precision.
export const negateAmount = <T>(value: T): T => {
  if (!isNumeric(value) || Number(value) === 0) {
    return value;
  }
  if (typeof value === 'number') {
    return -value as T;
  }
  const text = value.trim();
  return (text.startsWith('-') ? text.slice(1) : `-${text.replace(/^\+/, '')}`) as T;
};

// A blank Type keeps the classification the row loaded with, because its sign alone decides it.
const showsNegated = (cashFlowType: unknown, investedAtLoad?: boolean) =>
  cashFlowType ? cashFlowType === CASH_FLOW_TYPE.INVESTED_CAPITAL : Boolean(investedAtLoad);

export const toDisplayAmount = <T>(cashFlowType: unknown, stored: T, investedAtLoad?: boolean): T =>
  showsNegated(cashFlowType, investedAtLoad) ? negateAmount(stored) : stored;

export const toStoredAmount = <T>(cashFlowType: unknown, displayed: T, investedAtLoad?: boolean): T =>
  showsNegated(cashFlowType, investedAtLoad) ? negateAmount(displayed) : displayed;

interface LedgerCell {
  alias?: string;
  rowNumber?: number;
  value?: unknown;
}

// Pre-FX sign rule for a row, read from the live Type so it follows edits made since load.
export const rowAmountSign = (
  amountCell: LedgerCell & { investedAtLoad?: boolean; sheet?: { cells?: unknown } }
): PreFxAmountSign => {
  const typeCell = Object.values((amountCell.sheet?.cells ?? {}) as Record<string, LedgerCell>).find(
    cell => cell.alias === CashFlowColumnAlias.CASH_FLOW_TYPE && cell.rowNumber === amountCell.rowNumber
  );
  return {
    toDisplay: stored => toDisplayAmount(typeCell?.value, stored, amountCell.investedAtLoad),
    toStored: displayed => toStoredAmount(typeCell?.value, displayed, amountCell.investedAtLoad),
  };
};

// Before saving, gives a just-edited pre-FX amount the same sign as its row's stored amount, because
// the backend derives the amount from any pre-FX value it receives. Untouched annotations stay unsent.
export const alignPreFxSigns = (items: Pick<CashFlowLedgerItem, 'id' | 'amount'>[]) => {
  const store = useCellAnnotationStore.getState();
  items.forEach(({ id, amount }) => {
    const cellKey = id ? buildCellKey('cashflowledgeritem', id, 'amount', 'pre_fx_currency') : '';
    const state = store.states[cellKey];
    const preFx = Number(state?.rows.find(row => !row.isPlaceholder)?.value);
    const stored = Number(amount);
    if (!state?.isDirty || !preFx || !stored || Math.sign(preFx) === Math.sign(stored)) {
      return;
    }
    store.setRows(cellKey, state.rows.map(row => ({ ...row, value: row.value == null ? row.value : -Number(row.value) })));
  });
};
